import React, { FC, useState, useEffect } from "react";
import styled from "styled-components/native";
import { Helmet } from "react-helmet";
import HeaderWithTitle from "components/HeaderWithTitle";
import HeaderWithBackForward from "components/HeaderWithBackForward";
import { useHistory, useParams } from "react-router-dom";
import QuestionListModal from "components/QuestionListModal";
import PracticeTip from "components/PracticeTip";
import { IoMdArrowRoundUp, IoMdArrowRoundDown } from "react-icons/io";
import Colors from "theme/colors";
import useWindowDimensions from "hooks/useWindowDimensions";
import { scrollToTop, scrollToBottom, onScroll } from "utils/scroll";
import Fonts from "theme/fonts";
import { IoMdHome, IoIosArrowForward } from "react-icons/io";
import BeatLoader from "react-spinners/BeatLoader";
import { css } from "@emotion/core";
import { DEFAULT_TOEIC_YEAR } from "config/defaultYear";

interface StyledProps {
  size?: number;
  height?: number;
  width?: number;
  marginHorizontal?: number;
  paddingHorizontal?: number;
  paddingRight?: number;
  isCorrect?: boolean;
  isSelected?: boolean;
  isShown?: boolean;
  marginTop?: number;
  marginLeft?: number;
}

const Container = styled.View`
  display: flex;
  width: 100%;
  min-height: 100%;
  background-color: ${Colors.SECOND_COLOR};
`;

const FloatingButton = styled.TouchableOpacity<StyledProps>`
  margin-top: ${(props) => props.marginTop}px;
  margin-left: ${(props) => props.marginLeft}px;
  padding: 10px;
  position: absolute;
  position: fixed;
  background-color: ${Colors.WHITE};
  border-radius: 24px;
  border-width: 1px;
  border-color: ${Colors.CARD_BORDER};
`;

const ListContainer = styled.View<StyledProps>`
  width: ${(props) => props.width}px;
  max-width: 900px;
  padding-top: 8px;
  padding-bottom: 32px;
  align-self: center;
`;

const Navigator = styled.View<StyledProps>`
  width: ${(props) => props.width}px;
  max-width: 900px;
  flex-direction: row;
  align-self: center;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
  margin-top: 16px;
  margin-bottom: 8px;
  padding-horizontal: 8px;
`;

const Button = styled.TouchableOpacity`
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const ButtonText = styled(Fonts.Bold)`
  color: ${Colors.MAIN_COLOR};
  text-align: center;
  font-size: 14px;
  padding-horizontal: 6px;
`;

const CrumbMuted = styled(Fonts.Normal)`
  color: ${Colors.MUTED_TEXT};
  font-size: 14px;
  padding-horizontal: 6px;
`;

const overrideLoading = css`
  display: block;
  margin: 40px auto;
`;

interface Props {
  title: string;
  canBack?: boolean;
  showMenu?: boolean;
  disableBack?: boolean;
  disableForward?: boolean;
  showArrow?: boolean;
  showScrollToTop?: boolean;
  showScrollToBottom?: boolean;
  onBackDetail?: () => void;
  onForwardDetail?: () => void;
  isLoading?: boolean;
}

const Screen: FC<Props> = ({
  children,
  title,
  canBack,
  showMenu,
  showArrow,
  disableBack,
  disableForward,
  onBackDetail,
  onForwardDetail,
  showScrollToTop,
  showScrollToBottom,
  isLoading = false,
}) => {
  const history = useHistory();
  const { testNumber, partNumber, questionNumber, year } = useParams();

  const [isShowMenu, setIsShowMenu] = useState(false);
  const [isHiddenBackToTop, setIsHiddenBackToTop] = useState(true);
  const [isHiddenBackToBottom, setIsHiddenBackToBottom] = useState(false);
  const onShowMenu = () => {
    setIsShowMenu(true);
  };

  const { width, height } = useWindowDimensions();
  const contentWidth = width < 520 ? (width * 98) / 100 : Math.min((width * 80) / 100, 900);

  const goHome = () => {
    history.push(`/toeic${year || DEFAULT_TOEIC_YEAR}`);
  };

  const goTest = () => {
    if (testNumber) {
      history.push(`/toeic${year || DEFAULT_TOEIC_YEAR}/test${testNumber}`);
    }
  };

  const goPart = () => {
    if (testNumber && partNumber) {
      history.push(`/toeic${year || DEFAULT_TOEIC_YEAR}/test${testNumber}/part${partNumber}`);
    }
  };

  const onScrollToTop = () => {
    setIsHiddenBackToTop(true);
    setIsHiddenBackToBottom(false);
    scrollToTop();
  };

  const onScrollToBottom = () => {
    setIsHiddenBackToTop(false);
    setIsHiddenBackToBottom(true);
    scrollToBottom();
  };

  const onScrollEvent = (pageYOffset, scrollHeight) => {
    const distance = (height * 4) / 100;
    setIsHiddenBackToTop(pageYOffset <= distance);
    setIsHiddenBackToBottom(pageYOffset + height + distance >= scrollHeight);
  };

  useEffect(() => {
    onScroll(onScrollEvent);
  }, []);

  const showPartTip = Boolean(partNumber) && !questionNumber;

  return (
    <Container>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      {!showArrow && (
        <HeaderWithTitle
          title={title}
          canBack={!!canBack}
          onBack={() => history.goBack()}
          showMenu={showMenu}
          onShowMenu={() => onShowMenu()}
        />
      )}
      {showArrow && (
        <HeaderWithBackForward
          title={title}
          canBack={!!canBack}
          onBack={() => history.goBack()}
          showArrow={showArrow}
          onBackDetail={onBackDetail}
          onForwardDetail={onForwardDetail}
          disableBack={disableBack}
          disableForward={disableForward}
        />
      )}
      <QuestionListModal
        modalVisible={isShowMenu}
        setModalVisible={() => setIsShowMenu(false)}
      />
      {width >= 520 && (
        <Navigator width={contentWidth}>
          <Button onPress={goHome} accessibilityLabel="Về danh sách test">
            <IoMdHome
              color={Colors.MAIN_COLOR}
              size={16}
              style={{ marginBottom: 2 }}
            />
            <ButtonText>{`TOEIC ${year || DEFAULT_TOEIC_YEAR}`}</ButtonText>
          </Button>
          {testNumber && testNumber > 0 && (
            <IoIosArrowForward color={Colors.MUTED_TEXT} size={14} />
          )}
          {testNumber && testNumber > 0 && testNumber <= 10 && (
            <Button onPress={goTest}>
              <ButtonText>{`Test ${testNumber}`}</ButtonText>
            </Button>
          )}
          {partNumber && partNumber > 0 && (
            <IoIosArrowForward color={Colors.MUTED_TEXT} size={14} />
          )}
          {partNumber && partNumber > 0 && partNumber <= 7 && (
            <Button onPress={goPart}>
              <ButtonText>{`Part ${partNumber}`}</ButtonText>
            </Button>
          )}
          {questionNumber && questionNumber > 0 && (
            <IoIosArrowForward color={Colors.MUTED_TEXT} size={14} />
          )}
          {questionNumber && questionNumber > 0 && (
            <CrumbMuted>{`Câu ${questionNumber}`}</CrumbMuted>
          )}
        </Navigator>
      )}

      <ListContainer width={contentWidth}>
        {isLoading ? (
          <BeatLoader
            css={overrideLoading}
            size={12}
            color={Colors.MAIN_COLOR}
            loading={true}
          />
        ) : (
          <>
            {showPartTip ? <PracticeTip partNumber={partNumber} /> : null}
            {children}
          </>
        )}
      </ListContainer>
      {width >= 520 && showScrollToTop && !isHiddenBackToTop && (
        <FloatingButton
          marginLeft={(width * 90) / 100}
          marginTop={(height * 2) / 100}
          onPress={() => onScrollToTop()}
          accessibilityLabel="Lên đầu trang"
        >
          <IoMdArrowRoundUp color={Colors.MAIN_COLOR} size={24} />
        </FloatingButton>
      )}
      {width >= 520 && showScrollToBottom && !isHiddenBackToBottom && (
        <FloatingButton
          marginLeft={(width * 90) / 100}
          marginTop={(height * 92) / 100}
          onPress={() => onScrollToBottom()}
          accessibilityLabel="Xuống cuối trang"
        >
          <IoMdArrowRoundDown color={Colors.MAIN_COLOR} size={24} />
        </FloatingButton>
      )}
    </Container>
  );
};

Screen.defaultProps = {
  canBack: false,
  showMenu: false,
  showScrollToTop: false,
  showScrollToBottom: false,
};

export default Screen;
